import React from 'react';
import { useForm } from 'react-hook-form';
import { AuthContextType } from '../../../features/auth/AuthContext';
import Modal from '../../../shared/ui/Modal';
import {
    fetchShopLoginInfos,
    createShopLoginInfo,
    updateShopLoginInfo,
    deleteShopLoginInfo,
    ShopLoginInfoPayload,
} from '../../../api/shoplogininfos';

interface ShopLoginInfoFormProps {
    auth: AuthContextType;
}

interface ShopLoginInfoData extends ShopLoginInfoPayload {
    id: string;
    created_at: string;
    updated_at: string | null;
}

const marketNames: Record<string, string> = {
    smartstore: '스마트스토어',
    auction: '옥션',
    gmarket: '지마켓',
    eleventh: '11번가',
    ssgdotcom: 'SSG닷컴',
};

const ShopLoginInfoForm: React.FC<ShopLoginInfoFormProps> = ({ auth }) => {
    const [loginInfos, setLoginInfos] = React.useState<ShopLoginInfoData[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [processMessage, setProcessMessage] = React.useState<string | null>(null);
    const [editingId, setEditingId] = React.useState<string | null>(null);
    const [showForm, setShowForm] = React.useState(false);

    const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<ShopLoginInfoPayload>({
        defaultValues: {
            market_site: 'smartstore',
            login_id: '',
            login_pw: '',
        },
    });

    const loadLoginInfos = React.useCallback(() => {
        setIsLoading(true);
        fetchShopLoginInfos({
            auth,
            setErrorMessage,
            setResult: (data) => {
                if (Array.isArray(data)) {
                    setLoginInfos(data);
                } else {
                    setLoginInfos([]);
                }
            },
        }).finally(() => setIsLoading(false));
    }, [auth]);

    React.useEffect(() => {
        loadLoginInfos();
    }, [loadLoginInfos]);

    const onSubmit = async (data: ShopLoginInfoPayload) => {
        setErrorMessage(null);
        if (editingId) {
            try {
                await updateShopLoginInfo({
                    auth,
                    id: editingId,
                    payload: data,
                    setErrorMessage,
                    setProcessMessage,
                    setResult: () => {
                        loadLoginInfos();
                        handleCancel();
                    },
                });
            } catch {
                // ignore, setErrorMessage handles it
            }
        } else {
            try {
                await createShopLoginInfo({
                    auth,
                    payload: data,
                    setErrorMessage,
                    setProcessMessage,
                    setResult: () => {
                        loadLoginInfos();
                        handleCancel();
                    },
                });
            } catch {
                // ignore, setErrorMessage handles it
            }
        }
    };

    const handleEdit = (item: ShopLoginInfoData) => {
        setEditingId(item.id);
        setValue('market_site', item.market_site);
        setValue('login_id', item.login_id);
        setValue('login_pw', item.login_pw);
        setShowForm(true);
    };

    const handleDelete = async (id: string) => {
        if (!confirm('삭제하시겠습니까?')) return;
        setErrorMessage(null);
        try {
            await deleteShopLoginInfo({
                auth,
                id,
                setErrorMessage,
                setProcessMessage,
                setResult: () => {
                    loadLoginInfos();
                    if (editingId === id) {
                        handleCancel();
                    }
                },
            });
        } catch {
            // ignore, setErrorMessage handles it
        }
    };

    const handleCancel = () => {
        reset({ market_site: 'smartstore', login_id: '', login_pw: '' });
        setEditingId(null);
        setErrorMessage(null);
        setShowForm(false);
    };

    const handleAdd = () => {
        handleCancel();
        setShowForm(true);
    };

    return (<>
        {errorMessage && !showForm && (
            <div className="alert alert-danger" role="alert">
                {errorMessage}
            </div>
        )}
        {processMessage && !showForm && (
            <div className="alert alert-info" role="alert">
                {processMessage}
            </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-1">
            <div>상점 로그인 설정 목록</div>
            <button type="button" className="btn btn-primary" onClick={handleAdd}>추가</button>
        </div>
        <table className="table text-center">
                    <thead>
                        <tr>
                            <th className="fw-normal">#</th>
                            <th className="fw-normal">마켓</th>
                            <th className="fw-normal">로그인 ID</th>
                            <th className="fw-normal">로그인 비밀번호</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={5} className="text-center align-middle" style={{ height: '5rem' }}>
                                    로딩 중입니다.
                                </td>
                            </tr>
                        ) : loginInfos.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="text-center align-middle" style={{ height: '5rem' }}>
                                    등록된 상점 로그인 설정이 없습니다.
                                </td>
                            </tr>
                        ) : (
                            loginInfos.map((item, idx) => (
                                <tr key={item.id}>
                                    <td>{idx + 1}</td>
                                    <td>{marketNames[item.market_site] || item.market_site}</td>
                                    <td>{item.login_id}</td>
                                    <td>{item.login_pw ? '••••••••' : '-'}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => handleEdit(item)}
                                        >
                                            수정
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger ms-1"
                                            onClick={() => handleDelete(item.id)}
                                        >
                                            삭제
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
        </table>
        <Modal show={showForm} onHide={handleCancel} scrollable>
            <Modal.Header closeButton>
                <Modal.Title>{editingId ? '상점 로그인 설정 수정' : '상점 로그인 설정 추가'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && (
                    <div className="alert alert-danger" role="alert">{errorMessage}</div>
                )}
                {processMessage && (
                    <div className="alert alert-info" role="alert">{processMessage}</div>
                )}
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <ul className="list-unstyled mt-2">
                        <li className="mb-3">
                            <label htmlFor="id_market_site" className="form-label">마켓</label>
                            <select
                                id="id_market_site"
                                className={`form-select ${errors.market_site ? 'is-invalid' : ''}`}
                                {...register('market_site', { required: '마켓을 선택하세요.' })}
                            >
                                <option value="smartstore">스마트스토어</option>
                                <option value="auction">옥션</option>
                                <option value="gmarket">지마켓</option>
                                <option value="eleventh">11번가</option>
                                <option value="ssgdotcom">SSG닷컴</option>
                            </select>
                            {errors.market_site && <div className="invalid-feedback">{errors.market_site.message}</div>}
                        </li>
                        <li className="mb-3">
                            <label htmlFor="id_login_id" className="form-label">로그인 ID</label>
                            <input
                                type="text"
                                id="id_login_id"
                                className={`form-control ${errors.login_id ? 'is-invalid' : ''}`}
                                {...register('login_id', {
                                    required: '로그인 ID를 입력하세요.',
                                    minLength: { value: 4, message: '로그인 ID는 4자 이상 입력하세요.' },
                                })}
                            />
                            {errors.login_id && <div className="invalid-feedback">{errors.login_id.message}</div>}
                        </li>
                        <li className="mb-3">
                            <label htmlFor="id_login_pw" className="form-label">로그인 비밀번호</label>
                            <input
                                type="password"
                                id="id_login_pw"
                                className={`form-control ${errors.login_pw ? 'is-invalid' : ''}`}
                                {...register('login_pw', {
                                    required: '로그인 비밀번호를 입력하세요.',
                                    minLength: { value: 4, message: '로그인 비밀번호는 4자 이상 입력하세요.' },
                                })}
                            />
                            {errors.login_pw && <div className="invalid-feedback">{errors.login_pw.message}</div>}
                        </li>
                        <li className="d-flex gap-2">
                            <button type="submit" className="btn btn-primary">
                                {editingId ? '변경' : '추가'}
                            </button>
                            <button type="button" className="btn btn-outline-secondary" onClick={handleCancel}>
                                취소
                            </button>
                        </li>
                    </ul>
                </form>
            </Modal.Body>
        </Modal>
    </>);
};

export default ShopLoginInfoForm;
